import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReactFlow, {
  Controls,
  Background,
  applyNodeChanges,
  MiniMap,
  MarkerType,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { toPng } from "html-to-image";
import {
  MdAdd,
  MdDelete,
  MdArrowBack,
  MdImage,
  MdVisibility,
  MdAutoAwesome,
  MdDownload,
} from "react-icons/md";
import dagre from "dagre";
import oeeFlowchartService from "../../services/oeeFlowchartService";
import { authService } from "../../services";
import { aiService } from "../../services/aiService";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  showAppLoadingToast,
  appLoadingFailed,
  dismissAppLoadingToast,
} from "../../utils/appLoadingToast";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";

import "./FlowchartBuilder.css";

// --- Custom Diamond Node ---
const DiamondNode = ({ data }) => {
  return (
    <div
      style={{
        position: "relative",
        width: "100px",
        height: "100px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "white",
          border: "1.5px solid black",
          transform: "rotate(45deg)",
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "10px",
          textAlign: "center",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        {data.label}
      </div>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ background: "#000" }}
      />
    </div>
  );
};

// --- Custom Data (Parallelogram) Node ---
const DataNode = ({ data }) => {
  return (
    <div
      style={{
        position: "relative",
        width: "150px",
        height: "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "white",
          border: "1.5px solid black",
          transform: "skewX(-20deg)",
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "10px",
          textAlign: "center",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        {data.label}
      </div>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#000" }}
      />
    </div>
  );
};

// --- Custom Database (Cylinder) Node ---
const DatabaseNode = ({ data }) => {
  return (
    <div
      style={{
        position: "relative",
        width: "100px",
        height: "80px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "white",
          border: "1.5px solid black",
          borderRadius: "50% / 15%",
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "-10px",
          left: 0,
          width: "100%",
          height: "20px",
          backgroundColor: "white",
          border: "1.5px solid black",
          borderRadius: "50%",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          padding: "10px",
          paddingTop: "15px",
          textAlign: "center",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        {data.label}
      </div>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#000" }}
      />
    </div>
  );
};

// --- Custom Predefined Process Node ---
const PredefinedNode = ({ data }) => {
  return (
    <div
      style={{
        position: "relative",
        width: "150px",
        height: "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "white",
        border: "1.5px solid black",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "10px",
          top: 0,
          bottom: 0,
          width: "1.5px",
          backgroundColor: "black",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: "10px",
          top: 0,
          bottom: 0,
          width: "1.5px",
          backgroundColor: "black",
        }}
      />
      <div
        style={{
          position: "relative",
          padding: "10px",
          textAlign: "center",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        {data.label}
      </div>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#000" }}
      />
    </div>
  );
};

// --- Custom Document Node ---
const DocumentNode = ({ data }) => {
  return (
    <div
      style={{
        position: "relative",
        width: "150px",
        height: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "white",
        border: "1.5px solid black",
        borderBottom: 0,
      }}
    >
      <svg
        style={{
          position: "absolute",
          bottom: "-10px",
          left: "-0.75px",
          width: "calc(100% + 1.5px)",
          height: "15px",
        }}
        viewBox="0 0 100 20"
        preserveAspectRatio="none"
      >
        <path
          d="M0 0 Q 25 20 50 0 T 100 0 V 5 H 0 Z"
          fill="white"
          stroke="black"
          strokeWidth="1"
        />
      </svg>
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "10px",
          textAlign: "center",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        {data.label}
      </div>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#000", bottom: "-5px" }}
      />
    </div>
  );
};

// --- Custom Connector Node ---
const ConnectorNode = ({ data }) => {
  return (
    <div
      style={{
        position: "relative",
        width: "120px",
        height: "60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "45px",
          backgroundColor: "white",
          border: "1.5px solid black",
          borderBottom: 0,
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "15px",
          backgroundColor: "white",
          border: "1.5px solid black",
          borderTop: 0,
          clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)",
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "10px",
          textAlign: "center",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        {data.label}
      </div>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#000" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#000", bottom: "-5px" }}
      />
    </div>
  );
};

const initialNodes = [];
const initialEdges = [];

const OEEFlowchartBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewMode = window.location.pathname.includes("/view");

  // Form inputs
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState([
    {
      id: -1,
      stepName: "Start",
      stepType: "Start",
      nextStepOrder: null,
      yesStepOrder: null,
      noStepOrder: null,
    }, // Default start step
  ]);

  // Validation/Visual State
  const [generated, setGenerated] = useState(false);
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);

  // Image Generation States
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const flowRef = useRef(null);
  const hiddenFlowRef = useRef(null);

  const tenantId = authService.getTenantId();
  const hasAutoPreviewed = useRef(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-Generate Prompt State
  const [promptInput, setPromptInput] = useState("");
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);

  const [isEditorPreview, setIsEditorPreview] = useState(false);
  const initialCreationMode = location.state?.creationMode || "ai";
  const [creationMode, setCreationMode] = useState(initialCreationMode); // "ai" | "manual"
  const [hasGeneratedFromAI, setHasGeneratedFromAI] = useState(false);

  const showFormDetails =
    creationMode === "manual" ||
    hasGeneratedFromAI ||
    (id && id !== "new") ||
    !!location.state?.flowchartData;

  const handleGenerateFromPrompt = async () => {
    if (!promptInput || promptInput.trim() === "") {
      toast.warn("Please enter a prompt first.");
      return;
    }
    showAppLoadingToast("Generating flowchart steps…");
    setIsGeneratingPrompt(true);
    try {
      const result = await aiService.generateFlowchart(promptInput);
      if (result) {
        const resDesc = result.description || result.Description;
        const resSteps = result.steps || result.Steps;

        if (resDesc) {
          setDescription(resDesc);
        }
        if (resSteps && resSteps.length > 0) {
          const newSteps = resSteps.map((s, index) => ({
            id: -1 * (index + 1 + Math.floor(Math.random() * 1000)),
            stepName: s.stepName || s.StepName || `Step ${index + 1}`,
            stepType: s.stepType || s.StepType || "Process",
            nextStepOrder:
              s.nextStepOrder !== undefined
                ? s.nextStepOrder
                : s.NextStepOrder || null,
            yesStepOrder:
              s.yesStepOrder !== undefined
                ? s.yesStepOrder
                : s.YesStepOrder || null,
            noStepOrder:
              s.noStepOrder !== undefined
                ? s.noStepOrder
                : s.NoStepOrder || null,
          }));
          setSteps(newSteps);
          if (!title) {
            setTitle(
              promptInput.substring(0, 40) +
              (promptInput.length > 40 ? "..." : ""),
            );
          }
          setHasGeneratedFromAI(true);
        } else {
          toast.warn("AI did not return any steps.");
        }
      }
    } catch (err) {
      console.error(err);
      appLoadingFailed("Failed to auto-generate steps. Please try again.");
    } finally {
      dismissAppLoadingToast();
      setIsGeneratingPrompt(false);
    }
  };

  useEffect(() => {
    // Fast Fetching: Check if data was passed in route state AND it has the steps
    const preLoadedData = location.state?.flowchartData;
    const hasSteps =
      (preLoadedData?.steps && preLoadedData.steps.length > 0) ||
      (preLoadedData?.Steps && preLoadedData.Steps.length > 0);

    if (preLoadedData && hasSteps) {
      console.log("Using Pre-loaded Flowchart Data:", preLoadedData);
      processData(preLoadedData);
    } else if (id && id !== "new") {
      loadFlowchart(id);
    }
  }, [id, location.state]);

  const processData = (data) => {
    const fetchedTitle =
      data.title || data.Title || data.flowchartName || data.name || "";
    const fetchedDesc = data.description || data.Description || data.desc || "";
    setTitle(fetchedTitle);
    setDescription(fetchedDesc);

    const stepsData = data.steps || data.Steps || [];

    if (stepsData.length > 0) {
      const idToOrderMap = {};
      stepsData.forEach((s) => {
        idToOrderMap[s.id || s.Id] = s.stepOrder || s.StepOrder;
      });

      const mappedSteps = stepsData.map((s) => ({
        id: s.id || s.Id,
        stepName: s.stepName || s.StepName || "",
        stepType: s.stepType || s.StepType || "Process",
        nextStepOrder:
          s.nextStepId || s.NextStepId
            ? idToOrderMap[s.nextStepId || s.NextStepId]
            : null,
        yesStepOrder:
          s.yesStepId || s.YesStepId
            ? idToOrderMap[s.yesStepId || s.YesStepId]
            : null,
        noStepOrder:
          s.noStepId || s.NoStepId
            ? idToOrderMap[s.noStepId || s.NoStepId]
            : null,
      }));
      setSteps(mappedSteps);
      rebuildDiagram(mappedSteps);
    }
  };

  const loadFlowchart = async (flowchartId) => {
    try {
      setLoading(true);
      const rawData = await oeeFlowchartService.getById(flowchartId);
      let data = rawData?.data || rawData || {};
      if (Array.isArray(data) && data.length > 0) data = data[0];
      else if (Array.isArray(rawData) && rawData.length > 0) data = rawData[0];

      processData(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load flowchart data from the server.");
    } finally {
      setLoading(false);
    }
  };

  const nodeTypes = useMemo(
    () => ({
      diamond: DiamondNode,
      data: DataNode,
      database: DatabaseNode,
      predefined: PredefinedNode,
      document: DocumentNode,
      connector: ConnectorNode,
    }),
    [],
  );

  const handleAddStep = () => {
    const newStep = {
      id: -1 * (steps.length + 1 + Math.floor(Math.random() * 1000)), // Temp ID
      stepName: "",
      stepType: "Process",
      nextStepOrder: null,
      yesStepOrder: null,
      noStepOrder: null,
    };
    setSteps([...steps, newStep]);
  };

  const handleRemoveStep = (index) => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  // Logic to update references (Next/Yes/No)
  // We will use "Row Index + 1" (Step Order) as the reference in the UI Dropdowns

  const getLayoutedElements = (nodes, edges) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    // Use 'network-simplex' or 'longest-path' for more predictable flowchart layout
    dagreGraph.setGraph({
      rankdir: "TB",
      ranker: "network-simplex",
      nodesep: 150, // More horizontal space to prevent overlap
      ranksep: 100, // More vertical space for clear lines
    });

    nodes.forEach((node) => {
      let width = 180;
      let height = 80;
      if (node.type === "diamond") {
        width = 120;
        height = 120;
      }
      dagreGraph.setNode(node.id, { width, height });
    });

    edges.forEach((edge) => {
      const weight = edge.data?.weight || 1;
      dagreGraph.setEdge(edge.source, edge.target, { weight });
    });

    dagre.layout(dagreGraph);

    nodes.forEach((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      const w = node.type === "diamond" ? 120 : node.style?.width || 180;
      const h = node.type === "diamond" ? 120 : node.style?.height || 80;

      node.position = {
        x: nodeWithPosition.x - w / 2,
        y: nodeWithPosition.y - h / 2,
      };
      return node;
    });

    return { nodes, edges };
  };

  const handleGenerateImage = async (mode = "download") => {
    // Before generating, we need to make sure the graph geometry is built
    if (!validateForm()) return;
    rebuildDiagram(steps);

    // Give ReactFlow a tiny moment to mount and fitView the hidden div
    await new Promise((resolve) => setTimeout(resolve, 800));

    if (!hiddenFlowRef.current) return;

    try {
      window.scrollTo(0, 0);
      setIsCapturing(true);
      // Brief wait for UI components (controls/grid) to hide
      await new Promise((resolve) => setTimeout(resolve, 100));

      const dataUrl = await toPng(hiddenFlowRef.current, {
        cacheBust: true,
        backgroundColor: "#ffffff",
        quality: 1.0,
        pixelRatio: 3, // High resolution
        style: {
          transform: "scale(1)", // Ensure no unwanted scaling from the DOM
          transformOrigin: "top left",
          width: hiddenFlowRef.current.offsetWidth + "px",
          height: hiddenFlowRef.current.offsetHeight + "px",
        },
      });

      setIsCapturing(false);

      if (mode === "download") {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${title || "flowchart_diagram"}.png`;
        link.click();
      }
    } catch (err) {
      console.error("Failed to generate image:", err);
      toast.error("Failed to generate image.");
      setIsCapturing(false);
    }
  };

  const validateForm = () => {
    if (!title || title.trim() === "") {
      toast.warn("Please enter a flowchart title");
      return false;
    }
    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].stepName) {
        toast.warn(`Row ${i + 1}: Step Name is required`);
        return false;
      }
    }
    return true;
  };

  const generateFlowchart = () => {
    if (!validateForm()) return;
    rebuildDiagram(steps);
    saveToDatabase();
  };

  const rebuildDiagram = (currentSteps) => {
    // Generate Nodes and Edges
    let newNodes = [];
    let newEdges = [];

    currentSteps.forEach((step, index) => {
      const nodeId = (index + 1).toString(); // Visual ID (1-based index from list)

      let shapeStyle = {
        width: 150,
        padding: "10px",
        textAlign: "center",
        border: "1.5px solid black",
        fontSize: "12px",
        background: "white",
        color: "black",
        fontWeight: "bold",
        fontFamily: "Inter, system-ui, sans-serif",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "40px",
      };

      let type = "default";
      let label = step.stepName;

      if (step.stepType === "Start" || step.stepType === "End") {
        type = "default";
        shapeStyle = {
          ...shapeStyle,
          borderRadius: "30px",
          width: 130,
          height: 50,
        };
      } else if (step.stepType === "Decision") {
        type = "diamond";
        shapeStyle = {};
      } else if (step.stepType === "Data") {
        type = "data";
        shapeStyle = {};
      } else if (step.stepType === "Database") {
        type = "database";
        shapeStyle = {};
      } else if (step.stepType === "Predefined") {
        type = "predefined";
        shapeStyle = {};
      } else if (step.stepType === "Document") {
        type = "document";
        shapeStyle = {};
      } else if (step.stepType === "Connector") {
        type = "connector";
        shapeStyle = {};
      } else {
        // Process -> rectangle
        shapeStyle = {
          ...shapeStyle,
          borderRadius: 0,
          height: 50,
        };
      }

      newNodes.push({
        id: nodeId,
        data: { label: label },
        position: { x: 0, y: 0 },
        type: type,
        style: Object.keys(shapeStyle).length > 0 ? shapeStyle : undefined,
      });

      // Edges
      const source = nodeId;

      // Common edge styles
      const edgeStyle = { stroke: "#000", strokeWidth: 1.5 };
      const markerEnd = { type: MarkerType.ArrowClosed, color: "#000" };
      const edgeType = "smoothstep"; // Using smoothstep for better readability with crossing lines

      if (step.stepType === "Decision") {
        if (step.yesStepOrder) {
          newEdges.push({
            id: `e${source}-${step.yesStepOrder}-yes`,
            source: source,
            target: step.yesStepOrder.toString(),
            sourceHandle: "bottom", // Main flow
            label: "Yes",
            labelStyle: { fill: "#000", fontWeight: "bold", fontSize: 11 },
            labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
            labelBgPadding: [4, 2],
            style: edgeStyle,
            type: edgeType,
            markerEnd: markerEnd,
            data: { weight: 10 }, // High priority for Vertical alignment
          });
        }
        if (step.noStepOrder) {
          newEdges.push({
            id: `e${source}-${step.noStepOrder}-no`,
            source: source,
            target: step.noStepOrder.toString(),
            sourceHandle: "right", // Side flow
            label: "No",
            labelStyle: { fill: "#000", fontWeight: "bold", fontSize: 11 },
            labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
            labelBgPadding: [4, 2],
            style: edgeStyle,
            type: edgeType,
            markerEnd: markerEnd,
            data: { weight: 1 }, // Low priority, can branch out
          });
        }
      } else if (step.stepType !== "End") {
        if (step.nextStepOrder) {
          newEdges.push({
            id: `e${source}-${step.nextStepOrder}`,
            source: source,
            target: step.nextStepOrder.toString(),
            type: edgeType,
            style: edgeStyle,
            markerEnd: markerEnd,
            data: { weight: 10 }, // Main flow
          });
        }
      }
    });

    // Apply Layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      newNodes,
      newEdges,
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setGenerated(true);
  };

  const saveToDatabase = async () => {
    // validate
    if (!title || title.trim() === "") {
      toast.warn("Please enter a flowchart title");
      return;
    }

    const safeInt = (val) => {
      if (val === null || val === undefined || val === "") return null;
      const parsed = parseInt(val, 10);
      return isNaN(parsed) ? null : parsed;
    };

    const safeTenantId = tenantId ? parseInt(tenantId, 10) : 0;

    const payloadSteps = steps.map((s, idx) => ({
      id: 0,
      stepName: s.stepName,
      stepType: s.stepType,
      stepOrder: idx + 1,
      // PASS THE TARGET STEP ORDER INTO THE ID FIELDS
      nextStepId: safeInt(s.nextStepOrder),
      yesStepId: safeInt(s.yesStepOrder),
      noStepId: safeInt(s.noStepOrder),
      tenantId: safeTenantId,
    }));

    const payload = {
      id: id && id !== "new" ? parseInt(id, 10) : 0,
      title: title,
      description: description,
      tenantId: safeTenantId,
      steps: payloadSteps,
    };

    try {
      if (id && id !== "new") {
        await oeeFlowchartService.update(id, payload);
        toast.success("Flowchart updated successfully!");
      } else {
        await oeeFlowchartService.create(payload);
        toast.success("Flowchart created and saved successfully!");
      }

      navigate("/home/flowchart");
    } catch (err) {
      console.error("Save error:", err);
      const msg = err.response?.data?.title || "Error saving flowchart.";
      toast.error(msg);
    }
  };

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  if (isViewMode || isEditorPreview) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.75)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          backdropFilter: "blur(4px)",
        }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            width: "100%",
            maxWidth: "1400px",
            height: "100%",
            maxHeight: "95vh",
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Modal Header */}
          <div
            style={{
              padding: "20px 32px",
              borderBottom: "1px solid #f3f4f6",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#fff",
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "22px",
                  color: "#111827",
                  fontWeight: "700",
                }}
              >
                {title || "Flowchart Preview"}
              </h2>
            </div>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <button
                className="btn-premium btn-primary-action"
                onClick={() => {
                  if (isViewMode) {
                    navigate("/home/flowchart");
                  } else {
                    setIsEditorPreview(false);
                  }
                }}
              >
                <MdArrowBack size={20} />{" "}
                {isViewMode ? "Back to List" : "Close Preview"}
              </button>
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#fff",
              padding: "10px 32px",
              borderBottom: "1px solid #f3f4f6",
              display: "flex",
              gap: "15px",
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
              }}
            >
              Share via:
            </span>
            <a
              href={`mailto:?subject=${encodeURIComponent(title || "Flowchart")}&body=${encodeURIComponent(`Check out this flowchart: ${title || "Flowchart"}\n\nDescription: ${description}`)}`}
              className="btn btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                textDecoration: "none",
                color: "#111827",
              }}
            >
              ✉️ Email
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Check out this flowchart: ${title || "Flowchart"} - ${description}`)}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                textDecoration: "none",
                color: "#111827",
              }}
            >
              <FaWhatsapp color="#25D366" size={16} /> WhatsApp
            </a>
            {generated && (
              <button
                className="btn-premium btn-ai"
                onClick={() => handleGenerateImage("download")}
                disabled={isCapturing}
              >
                {isCapturing ? (
                  <div
                    className="spinner-small"
                    style={{
                      width: 16,
                      height: 16,
                      border: "2px solid #fff",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : (
                  <span className="ai-icon-wrapper">
                    <MdAutoAwesome size={18} />
                  </span>
                )}
                {isCapturing ? "Downloading..." : "AI Download"}
              </button>
            )}
          </div>

          {/* Modal Body (Flowchart) */}
          <div
            style={{
              flex: 1,
              position: "relative",
              backgroundColor: "#fafafa",
            }}
          >
            {generated ? (
              <div
                ref={flowRef}
                style={{ width: "100%", height: "100%", position: "relative" }}
              >
                {description && !isCapturing && (
                  <div
                    style={{
                      position: "absolute",
                      top: "20px",
                      left: "20px",
                      width: "350px",
                      backgroundColor: "#334155",
                      color: "#f8fafc",
                      borderRadius: "6px",
                      padding: "16px",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.5)",
                      zIndex: 10,
                      fontSize: "14px",
                      maxHeight: "80%",
                      overflowY: "auto",
                      fontFamily: "Inter, system-ui, sans-serif",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#e0f2fe",
                        fontWeight: "600",
                      }}
                    >
                      <span role="img" aria-label="pin">
                        📌
                      </span>{" "}
                      Overview
                    </h3>
                    <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                      {description.split("\n").map((line, idx) => {
                        const trimmed = line.trim();
                        if (
                          trimmed.startsWith("-") ||
                          trimmed.startsWith("•")
                        ) {
                          return (
                            <div
                              key={idx}
                              style={{
                                display: "flex",
                                gap: "8px",
                                marginBottom: "8px",
                                paddingLeft: "8px",
                              }}
                            >
                              <span style={{ color: "#9ca3af" }}>•</span>
                              <span>{trimmed.replace(/^[-•]\s*/, "")}</span>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={idx}
                            style={{
                              marginBottom: trimmed === "" ? "0" : "10px",
                              fontWeight: idx === 0 ? "600" : "normal",
                            }}
                          >
                            {trimmed}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={nodeTypes}
                  onNodesChange={undefined}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  fitView
                  fitViewOptions={{ padding: 0.1 }}
                  style={{ background: "#ffffff" }}
                >
                  {!isCapturing && <Background color="#e5e7eb" gap={20} />}
                  {!isCapturing && <Controls />}
                </ReactFlow>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div className="loading-spinner"></div>
                <p style={{ marginLeft: "12px", color: "#6b7280" }}>
                  Loading flowchart...
                </p>
              </div>
            )}
          </div>

          {/* Hidden Perfectly-Sized Capture Div for Downloads */}
          <div
            style={{
              position: "absolute",
              top: "-9999px",
              left: "-9999px",
              width: "1200px",
              height: "1000px",
              background: "#fff",
              opacity: 0,
              visibility: "visible",
            }}
          >
            <div ref={hiddenFlowRef} style={{ width: "100%", height: "100%" }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.1 }}
                style={{ background: "#ffffff" }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <PageLoading message="Loading flowchart builder..." />;
  }

  return (
    <div className="flowchart-builder-page">
      <div
        className="page-header"
      >
        <div>
          <h1>{id && id !== "new" ? "Edit Flowchart" : "New Flowchart"}</h1>
          <p>Create or update a process flow</p>
        </div>
        <div className="header-actions" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            className=" btn-primary btn"
            onClick={() => navigate("/home/flowchart")}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <MdArrowBack size={20} /> Back
          </button>
        </div>
      </div>

      <div className="fc-content">
        {!isViewMode && (
          <div className="fc-form-section">
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  creationMode === "ai" && showFormDetails
                    ? "minmax(0, 1.2fr) minmax(0, 0.8fr)"
                    : "1fr",
                gap: "30px",
                marginBottom: "30px",
              }}
            >
              <div className="left-inputs">
                {showFormDetails && (
                  <>
                    <div
                      className="form-group"
                      style={{ marginBottom: "20px" }}
                    >
                      <label>
                        Flowchart Title <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Downtime Analysis Process"
                        style={{
                          border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                          borderRadius: "8px",
                          padding: "12px",
                        }}
                      />
                    </div>

                    <div
                      className="form-group"
                      style={{ marginBottom: "20px" }}
                    >
                      <label>Description</label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional description"
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                          minHeight: "100px",
                          resize: "vertical",
                          fontFamily: "inherit",
                        }}
                      />
                      {!description &&
                        steps.length > 0 &&
                        steps[0].stepName !== "Start" &&
                        creationMode === "ai" && (
                          <button
                            type="button"
                            className="btn-premium btn-ai"
                            onClick={async () => {
                              if (
                                steps.length === 0 ||
                                steps[0].stepName === "Start"
                              )
                                return;
                              showAppLoadingToast("Generating summary…");
                              setIsAIGenerating(true);
                              try {
                                const stepsText = steps
                                  .map((s) => s.stepName)
                                  .join(" -> ");
                                const result =
                                  await aiService.generateFlowchart(
                                    `Generate a high level summary description for the following process flow: ${stepsText}. Return empty steps array.`,
                                  );
                                if (
                                  result &&
                                  (result.description || result.Description)
                                ) {
                                  setDescription(
                                    result.description || result.Description,
                                  );
                                }
                              } catch (err) {
                                console.error(err);
                              } finally {
                                dismissAppLoadingToast();
                                setIsAIGenerating(false);
                              }
                            }}
                            disabled={isAIGenerating}
                            style={{
                              marginTop: "10px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span className="ai-icon-wrapper">
                              <MdAutoAwesome size={14} />
                            </span>{" "}
                            Auto-Generate Summary
                          </button>
                        )}
                    </div>
                  </>
                )}

                {creationMode === "ai" && (
                  <div className="form-group">
                    <label>Create Your Flowchart With AI</label>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      <textarea
                        value={promptInput}
                        onChange={(e) => setPromptInput(e.target.value)}
                        placeholder="E.g., Creating a user login flow with email validation..."
                        style={{
                          flex: 1,
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                          minHeight: "80px",
                          resize: "vertical",
                          fontFamily: "inherit",
                        }}
                      />
                      <button
                        type="button"
                        className="btn-premium btn-ai"
                        onClick={handleGenerateFromPrompt}
                        disabled={isGeneratingPrompt}
                        style={{
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span className="ai-icon-wrapper">
                          <MdAutoAwesome size={18} />
                        </span>
                        Auto-Generate Steps
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {creationMode === "ai" && showFormDetails && (
                <div className="right-preview">
                  <label
                    style={{
                      color: "#64748b",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Live Preview
                  </label>
                  {description ? (
                    <div
                      style={{
                        backgroundColor: "#1e293b",
                        color: "#f8fafc",
                        borderRadius: "12px",
                        padding: "24px",
                        boxShadow:
                          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        fontSize: "14px",
                        minHeight: "100%",
                        fontFamily: "'Outfit', 'Inter', sans-serif",
                        border: "1px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      <h3
                        style={{
                          margin: "0 0 16px 0",
                          fontSize: "18px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          color: "#60a5fa",
                          fontWeight: "600",
                        }}
                      >
                        <span role="img" aria-label="pin">
                          📌
                        </span>{" "}
                        Overview
                      </h3>
                      <div
                        style={{ whiteSpace: "pre-wrap", lineHeight: "1.7" }}
                      >
                        {description.split("\n").map((line, idx) => {
                          const trimmed = line.trim();
                          if (
                            trimmed.startsWith("-") ||
                            trimmed.startsWith("•")
                          ) {
                            return (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  gap: "12px",
                                  marginBottom: "10px",
                                  paddingLeft: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    color: "#60a5fa",
                                    fontSize: "18px",
                                    lineHeight: "1",
                                  }}
                                >
                                  •
                                </span>
                                <span style={{ color: "#cbd5e1" }}>
                                  {trimmed.replace(/^[-•]\s*/, "")}
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div
                              key={idx}
                              style={{
                                marginBottom: trimmed === "" ? "0" : "15px",
                                fontWeight: idx === 0 ? "600" : "400",
                                fontSize: idx === 0 ? "15px" : "14px",
                                color: idx === 0 ? "#f1f5f9" : "#94a3b8",
                              }}
                            >
                              {trimmed}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        height: "100%",
                        minHeight: "200px",
                        border: "2px dashed #e2e8f0; border-color: var(--border-primary)",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94a3b8",
                        padding: "40px",
                        textAlign: "center",
                      }}
                    >
                      <MdAutoAwesome
                        size={40}
                        style={{ marginBottom: "15px", opacity: 0.3 }}
                      />
                      <p style={{ margin: 0, fontSize: "14px" }}>
                        Generate a description to see the premium overview
                        preview here.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {showFormDetails && (
              <>
                <div className="processstepsheader w-100">
                  <h3>Process Steps</h3>
                  <button className="btn btn-primary btnaddtep" onClick={handleAddStep}>
                    <MdAdd /> Add Step
                  </button>
                </div>
                <div className="table-wrapper">
                  <table className="steps-table">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Step Name</th>
                        <th>Type</th>
                        <th>Next Step</th>
                        <th>Yes (If Dec.)</th>
                        <th>No (If Dec.)</th>
                        <th style={{ width: 60 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {steps.map((step, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <input
                              type="text"
                              value={step.stepName}
                              onChange={(e) =>
                                handleStepChange(
                                  idx,
                                  "stepName",
                                  e.target.value,
                                )
                              }
                              placeholder="Name"
                            />
                          </td>
                          <td>
                            <select
                              value={step.stepType}
                              onChange={(e) =>
                                handleStepChange(
                                  idx,
                                  "stepType",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="Start">Oblong (Start)</option>
                              <option value="Process">
                                Operation (Rectangle)
                              </option>
                              <option value="Decision">
                                Decision (Diamond)
                              </option>
                              <option value="Data">Parallelogram (Data)</option>
                              <option value="Connector">
                                Connector (Circle)
                              </option>
                              <option value="End">Oblong (End)</option>
                            </select>
                          </td>
                          <td>
                            {step.stepType !== "Decision" &&
                              step.stepType !== "End" && (
                                <select
                                  value={step.nextStepOrder || ""}
                                  onChange={(e) =>
                                    handleStepChange(
                                      idx,
                                      "nextStepOrder",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">-- Select --</option>
                                  {steps.map((s, sIdx) => (
                                    <option key={sIdx} value={sIdx + 1}>
                                      {sIdx + 1}. {s.stepName || "Untitled"}
                                    </option>
                                  ))}
                                </select>
                              )}
                          </td>
                          <td>
                            {step.stepType === "Decision" && (
                              <select
                                value={step.yesStepOrder || ""}
                                onChange={(e) =>
                                  handleStepChange(
                                    idx,
                                    "yesStepOrder",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">-- Yes --</option>
                                {steps.map((s, sIdx) => (
                                  <option key={sIdx} value={sIdx + 1}>
                                    {sIdx + 1}. {s.stepName || "Untitled"}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            {step.stepType === "Decision" && (
                              <select
                                value={step.noStepOrder || ""}
                                onChange={(e) =>
                                  handleStepChange(
                                    idx,
                                    "noStepOrder",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">-- No --</option>
                                {steps.map((s, sIdx) => (
                                  <option key={sIdx} value={sIdx + 1}>
                                    {sIdx + 1}. {s.stepName || "Untitled"}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleRemoveStep(idx)}
                            >
                              <MdDelete />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                </div>


                <div
                  className="form-actions"
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      rebuildDiagram(steps);
                      setIsEditorPreview(true);
                    }}
                  >
                    <MdVisibility size={20} /> Preview Flowchart
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={generateFlowchart}
                  >
                    {id && id !== "new"
                      ? "Update Flowchart"
                      : "Create Flowchart"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {generated && (
          <div
            className="flow-canvas-container"
            style={{
              width: "2000px",
              height: `${Math.max(1200, steps.length * 200)}px`,
              position: "absolute",
              top: "-9999px",
              left: "-9999px",
              opacity: 0,
              visibility: "visible",
            }}
          >
            <div
              className="flow-canvas"
              ref={hiddenFlowRef}
              style={{
                width: "100%",
                height: "100%",
                background: "#ffffff",
              }}
            >
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                onNodesChange={undefined}
                nodesDraggable={false}
                nodesConnectable={false}
                fitView
                fitViewOptions={{ padding: 0.1 }}
                style={{ background: "#ffffff" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Flowchart Image Preview Modal */}
      <SharedModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Flowchart Image Preview"
        size="lg"
        footer={
          <button
            onClick={() => setShowPreviewModal(false)}
            className="btn btn-secondary"
          >
            Close
          </button>
        }
      >
        <div className="shared-modal-body">
          <div
            style={{
              overflow: "auto",
              border: "1px solid #ddd",
              borderRadius: "8px",
              backgroundColor: "#fafafa",
              padding: "10px",
            }}
          >
            {previewImage && (
              <img
                src={previewImage}
                alt="Flowchart Preview"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            )}
          </div>
        </div>
      </SharedModal>

      {/* Keep the modal strictly for 'Preview Flowchart' button on edit page */}

      {/* Keeping Saved List somewhat accessible or removing? User didn't ask for list, but usually good to have. 
                I'll put it at bottom or sidebar if space. 
                User focused on "Creation Page". 
                I'll leave it out of the main view to focus on the form as requested.
            */}
    </div>
  );
};

export default OEEFlowchartBuilder;
